import React from 'react';
import SectionHeader from './SectionHeader.jsx';

// Virtual-event locations ("Zoom", "Online") would still geocode to some
// arbitrary real place and render a misleading pin, so suppress the map
// when the location is clearly non-physical.
const VIRTUAL_LOCATION_PATTERN =
  /\b(online|virtual|zoom|google meet|teams|webex|livestream|webinar|tbd|tba)\b/i;

// Convert an IANA time zone (e.g. "America/Chicago") to its short abbreviation
// (e.g. "CDT"/"CST"), evaluated at the event's start date so DST is correct
// for when the event happens, not when the page is viewed.
const getTimeZoneAbbreviation = (timeZoneName, dateString) => {
  if (!timeZoneName) return '';
  try {
    // Anchor to noon UTC so the reference instant lands on the event's
    // calendar date regardless of the browser's local timezone.
    const referenceDate = dateString ? new Date(dateString + 'T12:00:00Z') : new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneName,
      timeZoneName: 'short',
    }).formatToParts(referenceDate);
    return parts.find((part) => part.type === 'timeZoneName')?.value || timeZoneName;
  } catch {
    // Not a valid IANA zone — fall back to showing the raw value
    return timeZoneName;
  }
};

/** "18:00" → { clock: "6:00", meridiem: "PM" }. */
const splitTime = (timeString) => {
  if (!timeString) return null;
  const [hours, minutes = '00'] = timeString.split(':');
  const hour24 = parseInt(hours, 10);
  if (Number.isNaN(hour24)) return null;
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  return { clock: `${hour12}:${minutes}`, meridiem: hour24 >= 12 ? 'PM' : 'AM' };
};

/**
 * The time line under the date (Paper "EventDateTile · IconTile"): "6:00 – 9:00 PM CST",
 * the meridiem written once when both ends share it.
 */
export const formatEventTimeRange = (startTime, endTime, timeZone, startDate) => {
  const start = splitTime(startTime);
  const end = splitTime(endTime);
  const zone = getTimeZoneAbbreviation(timeZone, startDate);
  let range = '';
  if (start && end) {
    range =
      start.meridiem === end.meridiem
        ? `${start.clock} – ${end.clock} ${end.meridiem}`
        : `${start.clock} ${start.meridiem} – ${end.clock} ${end.meridiem}`;
  } else if (start || end) {
    const only = start || end;
    range = `${only.clock} ${only.meridiem}`;
  }
  return [range, zone].filter(Boolean).join(' ');
};

const parseDate = (dateString) => (dateString ? new Date(dateString + 'T00:00:00') : null);

const formatDate = (dateString) => {
  if (!dateString) return '';
  return parseDate(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate) return 'TBD';

  const start = parseDate(startDate);
  const end = parseDate(endDate);

  // If no end date or same date, show single date
  if (!end || startDate === endDate) {
    return formatDate(startDate);
  }

  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    // Same month: "January 15 - 17, 2025"
    return `${start.toLocaleDateString('en-US', { month: 'long' })} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
  }
  if (sameYear) {
    // Same year: "January 15 - February 2, 2025"
    return `${start.toLocaleDateString('en-US', { month: 'long' })} ${start.getDate()} - ${end.toLocaleDateString('en-US', { month: 'long' })} ${end.getDate()}, ${start.getFullYear()}`;
  }
  // Different years: "December 30, 2024 - January 2, 2025"
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/** "Community Center, 1200 W Lake St, Chicago, IL" → venue + the rest of the address. */
const splitLocation = (location) => {
  const index = location.indexOf(',');
  if (index === -1) return { venue: location, address: '' };
  return { venue: location.slice(0, index).trim(), address: location.slice(index + 1).trim() };
};

const mapsSearchUrl = (location) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;

// Icon tiles: Lucide, 20, stroke 1.75, in the brand accent role (SPEC §C Icons; CON-528: the
// action fill is unreadable as an icon on white for light brands).
const TILE_ICON_CLASS =
  'size-[var(--cs-icon-lg)] text-[color:var(--cs-brand-accent,var(--cs-text-primary))] [stroke-width:var(--cs-icon-stroke)]';

const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className={TILE_ICON_CLASS} aria-hidden="true">
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const VideoIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className={TILE_ICON_CLASS} aria-hidden="true">
    <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
    <rect x="2" y="6" width="14" height="12" rx="2" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className={TILE_ICON_CLASS} aria-hidden="true">
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M3 10h18" />
  </svg>
);

const ArrowUpRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="size-[var(--cs-icon)] [stroke-width:var(--cs-icon-stroke)]" aria-hidden="true">
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);

// One family (Paper "EventDateTile · IconTile"): 44 square, radius 12, 1px edge.
const TILE_FRAME_CLASS =
  'size-[var(--cs-size-tile)] shrink-0 rounded-[var(--cs-control-radius)] border border-[color:var(--cs-border-control)]';

/** The date tile: white, a 14px brand-tint band with the month in caps, the day below. */
const EventDateTile = ({ date }) => (
  <div aria-hidden="true" className={`${TILE_FRAME_CLASS} flex flex-col overflow-hidden bg-[color:var(--cs-surface-0)]`}>
    <div className="flex h-[var(--cs-leading-14)] shrink-0 items-center justify-center bg-[color:var(--cs-brand-tint,var(--cs-surface-1))]">
      <span className="text-[length:var(--cs-text-2xs-plus)] leading-[var(--cs-leading-14)] font-semibold uppercase tracking-[var(--cs-tracking-caps)] text-[color:var(--cs-brand-text,var(--cs-text-primary))]">
        {date.toLocaleDateString('en-US', { month: 'short' })}
      </span>
    </div>
    <div className="flex flex-1 items-center justify-center">
      <span className="text-[length:var(--cs-text-lg)] leading-[var(--cs-leading-22)] font-semibold text-[color:var(--cs-text-primary)]">
        {date.getDate()}
      </span>
    </div>
  </div>
);

/** The icon tile: white like the date tile, a 20 Lucide glyph in the brand action role. */
const IconTile = ({ children }) => (
  <div aria-hidden="true" className={`${TILE_FRAME_CLASS} flex items-center justify-center bg-[color:var(--cs-surface-0)]`}>
    {children}
  </div>
);

const DetailRow = ({ tile, title, meta }) => (
  <div className="flex min-w-0 flex-1 items-center gap-[var(--cs-space-3)]">
    {tile}
    <div className="flex min-w-0 flex-col">
      <span className="text-[length:var(--cs-text-base)] leading-[var(--cs-leading-24)] font-medium text-[color:var(--cs-text-primary)]">
        {title}
      </span>
      {meta && (
        <span className="text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] text-[color:var(--cs-text-secondary)]">
          {meta}
        </span>
      )}
    </div>
  </div>
);

/**
 * EventTimeAndLocationDetails — the event's date and place (CON-518, Paper "EventDateTile ·
 * IconTile" + "Static map").
 *
 * Two parts that the hosted page places apart:
 * - `tiles`: the date tile + the location icon tile, side by side when the container is
 *   640 or wider (a container query, so a 448 pane stacks them even on a wide screen),
 *   placed below the header image. The address line ends with an arrow link to Google Maps.
 * - `map`: the "Location" section — SectionHeader + today's keyless Google Maps iframe
 *   (192, card radius, 1px edge, card shadow, the "Maps ↗" chip). Hidden for virtual places.
 *
 * @param {'all'|'tiles'|'map'} [part='all'] - Which part to render.
 * @param {boolean} [showMap=true] - Whether `all` includes the map section.
 */
const EventTimeAndLocationDetails = ({
  eventStartDate,
  eventEndDate,
  eventStartTime,
  eventEndTime,
  eventLocation,
  timeZone,
  showMap = true,
  part = 'all',
}) => {
  // A location naming a virtual platform can still be a physical venue
  // ("Zoom Video HQ, 55 Almaden Blvd, San Jose, CA"), so a street-address
  // shape (digits + comma separators) overrides the virtual-keyword check.
  const looksLikeStreetAddress =
    Boolean(eventLocation) && /\d/.test(eventLocation) && eventLocation.includes(',');
  const isMappableLocation =
    Boolean(eventLocation) &&
    (looksLikeStreetAddress || !VIRTUAL_LOCATION_PATTERN.test(eventLocation));

  const startDate = parseDate(eventStartDate);
  const timeLine =
    eventStartTime || eventEndTime
      ? formatEventTimeRange(eventStartTime, eventEndTime, timeZone, eventStartDate)
      : '';

  const tiles = (eventStartDate || timeLine || eventLocation) && (
    <div className="@container w-full">
      <div className="flex flex-col gap-[var(--cs-space-3)] @min-[40rem]:flex-row @min-[40rem]:gap-[var(--cs-space-6)]">
        {(eventStartDate || timeLine) && (
          <DetailRow
            tile={
              startDate && !Number.isNaN(startDate.getTime()) ? (
                <EventDateTile date={startDate} />
              ) : (
                <IconTile>
                  <CalendarIcon />
                </IconTile>
              )
            }
            title={formatDateRange(eventStartDate, eventEndDate)}
            meta={timeLine}
          />
        )}
        {eventLocation &&
          (isMappableLocation ? (
            <DetailRow
              tile={
                <IconTile>
                  <PinIcon />
                </IconTile>
              }
              title={splitLocation(eventLocation).venue}
              meta={
                <span className="inline-flex items-center gap-[var(--cs-space-2)]">
                  {splitLocation(eventLocation).address && (
                    <span>{splitLocation(eventLocation).address}</span>
                  )}
                  <a
                    href={mapsSearchUrl(eventLocation)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open location in Google Maps"
                    className="inline-flex shrink-0 rounded-[var(--cs-radius-sm)] text-[color:var(--cs-text-secondary)] hover:text-[color:var(--cs-text-primary)] focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]"
                  >
                    <ArrowUpRightIcon />
                  </a>
                </span>
              }
            />
          ) : (
            <DetailRow
              tile={
                <IconTile>
                  <VideoIcon />
                </IconTile>
              }
              title="Online event"
              meta={eventLocation}
            />
          ))}
      </div>
    </div>
  );

  const map = isMappableLocation && (
    <div className="flex flex-col gap-[var(--cs-space-3)]">
      <SectionHeader>Location</SectionHeader>
      <div
        className="cs-map-embed relative h-[var(--cs-size-map)] overflow-hidden rounded-[var(--cs-card-radius)] bg-[color:var(--cs-placeholder)]"
        style={{
          border: 'var(--header-image-border, 1px solid var(--cs-border-control))',
          boxShadow: 'var(--cs-card-shadow)',
        }}
      >
        {/* Keyless legacy embed endpoint — deliberate tradeoff to avoid a
            Google Cloud key/billing account. If Google ever degrades it,
            swap src to the official Maps Embed API (free, key required):
            https://www.google.com/maps/embed/v1/place?key=...&q=... */}
        <iframe
          title="Event location map"
          src={`https://maps.google.com/maps?q=${encodeURIComponent(eventLocation)}&output=embed`}
          className="pointer-events-none h-full w-full border-0"
          // Inline on purpose: the recurring-donation card stylesheet ships a global,
          // unlayered `iframe { width: 80% }`, which outranks every layered utility, so
          // `w-full` alone left the right fifth of the box empty.
          style={{ width: '100%', height: '100%' }}
          tabIndex={-1}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <a
          href={mapsSearchUrl(eventLocation)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open location in Google Maps"
          className="absolute inset-0 focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]"
        >
          {/* CON-514: the Maps chip — control radius, card shadow. It covers the embed's own
              "Maps" card (80x34 at an 8 inset, near-square corners; measured 2026-10-30). At a 4
              inset, 44 tall and at least 88 wide, it overhangs that card by 4 to 6 on every side,
              enough for the control radius to round outside Google's corners; a 40-tall chip
              overhangs the bottom by only 2 and lets a corner show. */}
          <span
            className="absolute left-1 top-1 inline-flex h-[var(--cs-space-11)] min-w-[calc(var(--cs-space-11)*2)] items-center justify-center gap-[var(--cs-space-1)] rounded-[var(--cs-control-radius)] bg-[color:var(--cs-surface-0)] px-[var(--cs-space-4)] text-[length:var(--cs-button-sm-font-size)] font-semibold leading-[var(--cs-button-sm-line-height)] shadow-[shadow:var(--cs-card-shadow)] transition-colors duration-[var(--cs-motion-instant)] hover:bg-[color:var(--cs-surface-1)]"
            style={{ color: 'var(--cs-brand-text, var(--cs-text-primary))' }}
          >
            Maps
            <ArrowUpRightIcon />
          </span>
        </a>
      </div>
    </div>
  );

  if (part === 'tiles') return tiles || null;
  if (part === 'map') return map || null;

  return (
    <div className="flex flex-col gap-[var(--cs-space-8)]">
      {tiles}
      {showMap && map}
    </div>
  );
};

export default EventTimeAndLocationDetails;
